import { motion } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { transitions } from '../lib/motion';
import { useI18n } from '../i18n';
import { cn, useIsDesktop } from '../lib/ui';
import {
  CairnMark,
  IconChart,
  IconChevronLeft,
  IconHome,
  IconPlus,
  IconReceipt,
  IconSettings,
} from './icons';
import { LedgerSwitcher } from './ledgers';

const TABS = [
  { to: '/', key: 'nav.overview', Icon: IconHome, end: true },
  { to: '/receipts', key: 'nav.receipts', Icon: IconReceipt, end: false },
  { to: '/add', key: 'nav.add', Icon: IconPlus, end: false },
  { to: '/insights', key: 'nav.insights', Icon: IconChart, end: false },
  { to: '/settings', key: 'nav.settings', Icon: IconSettings, end: false },
] as const;

/**
 * Mobile navigation: a translucent tab bar pinned to the bottom, where thumbs
 * are. The "Add" tab sits dead center because filing a receipt is the thing
 * people open this app to do.
 */
function TabBar() {
  const { t } = useI18n();

  return (
    <nav
      className="material-strong safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-[var(--line)] lg:hidden"
      aria-label={t('a11y.menu')}
    >
      <ul className="mx-auto flex max-w-lg items-stretch">
        {TABS.map(({ to, key, Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'press flex h-[52px] flex-col items-center justify-center gap-[3px]',
                  isActive ? 'text-[var(--accent)]' : 'text-[var(--ink-muted)]',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={23} strokeWidth={isActive ? 2 : 1.6} />
                  <span className="text-[10px] font-medium leading-none">
                    {t(key as 'nav.overview')}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Sidebar({ workspaceName }: { workspaceName: string }) {
  const { t } = useI18n();

  return (
    <aside className="fixed inset-y-0 left-0 hidden w-[248px] flex-col border-r border-[var(--line)] bg-[var(--surface)] px-3 py-6 lg:flex">
      <div className="mb-8 flex items-center gap-2.5 px-3">
        <CairnMark size={26} className="text-[var(--accent)]" />
        <div className="min-w-0">
          <div className="text-[15px] font-semibold tracking-tight text-[var(--ink)]">Cairn</div>
          {workspaceName ? (
            <div className="truncate text-[12px] text-[var(--ink-secondary)]">{workspaceName}</div>
          ) : null}
        </div>
      </div>

      <nav aria-label={t('a11y.menu')}>
        <ul className="space-y-0.5">
          {TABS.map(({ to, key, Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5 text-[14px] font-medium transition-colors',
                    isActive
                      ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                      : 'text-[var(--ink-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--ink)]',
                  )
                }
              >
                <Icon size={19} />
                {t(key as 'nav.overview')}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

/**
 * A large title that shrinks into the top bar as the page scrolls — the iOS
 * pattern. It gives the page a confident opening without stealing space once
 * you are reading.
 */
export function LargeTitle({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLElement>(null);

  /**
   * Collapse exactly when the large title reaches the bottom of the bar.
   *
   * The margin has to be the sticky header's real height, and the header is
   * `52px + env(safe-area-inset-top)` — which is 52 in a browser tab and closer
   * to 100 on a notched iPhone running this from the Home Screen. It used to be
   * a hardcoded -8px, so between the sentinel passing under the bar and passing
   * out of the viewport the header still thought it was expanded and stayed
   * transparent. The 32px title slid through that gap and straight across the
   * ledger switcher. On a desktop the gap is 44px and easy to miss; in a
   * standalone iOS app it is about 100px, which is the whole title.
   *
   * `rootMargin` cannot read `env()`, so the height is measured, and remeasured
   * when it changes — rotating the phone changes the inset.
   */
  useEffect(() => {
    const node = sentinel.current;
    const bar = header.current;
    if (!node || !bar) return;

    let observer: IntersectionObserver | null = null;

    const observe = () => {
      observer?.disconnect();
      // Round up: a fractional margin can leave a sub-pixel band where the
      // title is under the bar and the bar has not noticed.
      const height = Math.ceil(bar.getBoundingClientRect().height);
      observer = new IntersectionObserver(([entry]) => setCollapsed(!entry?.isIntersecting), {
        rootMargin: `-${height}px 0px 0px 0px`,
        threshold: 0,
      });
      observer.observe(node);
    };

    observe();

    const resize = new ResizeObserver(observe);
    resize.observe(bar);
    window.addEventListener('orientationchange', observe);

    return () => {
      observer?.disconnect();
      resize.disconnect();
      window.removeEventListener('orientationchange', observe);
    };
  }, []);

  return (
    <>
      <header
        ref={header}
        className={cn(
          // Always opaque, and always the page's own background colour.
          //
          // While expanded this bar used to be transparent, which is what let
          // the large title show through it and cross the ledger switcher — and
          // on iOS, where `safe-top` makes the bar ~100px tall, also let content
          // scroll visibly under the status bar. Since the background matches
          // the page exactly, painting it changes nothing you can see while
          // expanded and makes bleed-through impossible. Collapsing now only
          // brings in the divider, which is the whole of the effect anyway.
          'safe-top sticky top-0 z-30 bg-[var(--ground)] transition-[border-color] duration-200',
          collapsed ? 'border-b border-[var(--line)]' : 'border-b border-transparent',
        )}
      >
        <div className="flex h-[52px] items-center justify-between gap-3 px-4 lg:px-8">
          <motion.h1
            animate={{ opacity: collapsed ? 1 : 0, y: collapsed ? 0 : 6 }}
            transition={transitions.fade}
            className="truncate text-[17px] font-semibold tracking-tight text-[var(--ink)]"
          >
            {title}
          </motion.h1>
          <div className="flex shrink-0 items-center gap-2">
            <LedgerSwitcher />
            {action}
          </div>
        </div>
      </header>

      <div ref={sentinel} className="h-px" aria-hidden="true" />

      <div className="px-4 pb-2 pt-1 lg:px-8">
        <h2 className="text-[32px] font-semibold leading-tight tracking-[-0.02em] text-[var(--ink)]">
          {title}
        </h2>
        {subtitle ? (
          <p className="mt-1 text-[15px] leading-snug text-[var(--ink-secondary)]">{subtitle}</p>
        ) : null}
        {children}
      </div>
    </>
  );
}

/** A back-navigating header for detail screens. */
export function DetailHeader({
  title,
  onBack,
  action,
}: {
  title: string;
  onBack: () => void;
  action?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <header className="safe-top sticky top-0 z-30 border-b border-[var(--line)] bg-[var(--ground)]">
      <div className="flex h-[52px] items-center gap-2 px-2 lg:px-6">
        <button
          type="button"
          onClick={onBack}
          className="press flex items-center gap-0.5 rounded-full py-1.5 pl-1.5 pr-3 text-[15px] text-[var(--accent)]"
        >
          <IconChevronLeft size={22} />
          <span className="hidden sm:inline">{t('common.back')}</span>
        </button>
        <h1 className="min-w-0 flex-1 truncate text-center text-[16px] font-semibold text-[var(--ink)]">
          {title}
        </h1>
        <div className="flex min-w-[44px] justify-end pr-1">{action}</div>
      </div>
    </header>
  );
}

export function AppShell({
  workspaceName,
  children,
}: {
  workspaceName: string;
  children: ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const location = useLocation();

  // Every navigation should start at the top of the new page, the way a native
  // push does.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-dvh bg-[var(--ground)]">
      {isDesktop ? <Sidebar workspaceName={workspaceName} /> : null}
      <div className="lg:pl-[248px]">
        {/* Capped at a readable measure rather than filling the display: on a
            tablet the old 3xl ran the full width, so cards spanned 736px and
            body text ran on for a whole line. Below the cap — every phone —
            nothing changes. */}
        <main className="mx-auto max-w-2xl pb-[calc(env(safe-area-inset-bottom,0px)+72px)] lg:max-w-4xl lg:pb-16">
          {children}
        </main>
      </div>
      <TabBar />
    </div>
  );
}

/** Centerd, narrow layout for sign-in, setup and invitation screens. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-[var(--ground)] px-5 py-10">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitions.enter}
        className="w-full max-w-[380px]"
      >
        <div className="mb-7 flex flex-col items-center text-center">
          <CairnMark size={44} className="mb-4 text-[var(--accent)]" />
          <h1 className="text-[26px] font-semibold tracking-tight text-[var(--ink)]">{title}</h1>
          {subtitle ? (
            <p className="mt-2 text-[15px] leading-relaxed text-[var(--ink-secondary)]">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="card p-6">{children}</div>

        {footer ? <div className="mt-5 text-center">{footer}</div> : null}
      </motion.div>
    </div>
  );
}
